import React, { useState } from "react";
import { useIncomesInRange, useDeleteIncome, useCreateIncome, useUpdateIncome } from "../../hooks/income";
import { useCategoriesQuery } from "../../hooks/category";
import { useSearchParams } from "react-router-dom";
import {
  Box,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  Typography,
  Menu,
  MenuItem,
  Tooltip,
  Chip,
  TableContainer,
  Skeleton,
  useTheme,
  Fade,
  Avatar
} from "@mui/material";
import {
  Edit as EditIcon,
  FilterList as FilterListIcon,
  Delete as DeleteIcon,
  Add as AddIcon,
  AccountBalance as IncomeIcon} from "@mui/icons-material";
import Income from "../../models/finance/Income";
import {
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getMonthIndex} from "../../utils/DateUtil";
import IncomeFormDrawer from "../finance/IncomeFormDrawer";
import { CategoryType } from "../../models/finance/CategoryType";
import { useCurrencyFormatter } from "../../contexts/CurrencyContext";
import { GradientButton } from "../common/StyledComponents";
import { useError } from "../../error/useError";
import ErrorAlert from "../common/ErrorAlert";
import { useUserProfileContext } from "../../contexts/ProfileContext";

interface IncomeTableComponentProps {
  year: number;
  month: string;
  onIncomeChange?: () => void;
}

const IncomeTableComponent: React.FC<IncomeTableComponentProps> = ({
  year,
  month,
  onIncomeChange}) => {
  const theme = useTheme();
  const { formatCurrencyNumber: formatCurrency } = useCurrencyFormatter();
  const { formatDate } = useUserProfileContext();
  
  const { error, helpText, clearError } = useError();
  
  const startDate = getFirstDayOfMonth(year, getMonthIndex(month));
  const endDate = getLastDayOfMonth(year, getMonthIndex(month));
  
  const { data: incomes = [], isLoading: loading, refetch: refetchIncomes } = useIncomesInRange(startDate, endDate);
  const { data: categories = [] } = useCategoriesQuery(CategoryType.Income, true);
  const { mutate: deleteIncome } = useDeleteIncome();
  const { mutate: createIncome } = useCreateIncome();
  const { mutate: updateIncome } = useUpdateIncome();
  
  const [searchParams, setSearchParams] = useSearchParams();
  const [sortOrder, setSortOrder] = useState<"asc" | "desc">(
    (searchParams.get("sortOrder") as "asc" | "desc") || "desc"
  );
  const [sortBy, setSortBy] = useState<"date" | "amount" | "category">(
    (searchParams.get("sortBy") as "date" | "amount" | "category") || "date"
  );
  const [selectedCategory, setSelectedCategory] = useState<string>(
    searchParams.get("category") || ""
  );

  // Filter menu state
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  // Pagination state
  const [page, setPage] = useState(0);
  const rowsPerPage = 10;

  // Drawer logic
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerMode, setDrawerMode] = useState<"add" | "edit">("add");
  const [selectedIncome, setSelectedIncome] = useState<Income | undefined>(undefined);


  // Search params and sorting logic
  const updateSearchParams = (newParams: Record<string, string>) => {
    const updatedParams = new URLSearchParams(searchParams.toString());
    Object.entries(newParams).forEach(([key, value]) => {
      if (value) {
        updatedParams.set(key, value);
      } else {
        updatedParams.delete(key);
      }
    });
    setSearchParams(updatedParams);
  };

  const handleSort = (property: "date" | "amount" | "category") => {
    const isAsc = sortBy === property && sortOrder === "asc";
    const newOrder = isAsc ? "desc" : "asc";
    setSortBy(property);
    setSortOrder(newOrder);
    updateSearchParams({ sortBy: property, sortOrder: newOrder });
  };

  const handleCategoryFilter = (categoryTitle: string) => {
    setSelectedCategory(categoryTitle);
    setAnchorEl(null);
    updateSearchParams({ category: categoryTitle });
  };

  const clearCategoryFilter = () => {
    setSelectedCategory("");
    setAnchorEl(null);
    updateSearchParams({ category: "" });
  };

  // Filter and sort incomes
  const filteredIncomes = incomes.filter((income) =>
    selectedCategory ? income.category === selectedCategory : true
  );

  const sortedIncomes = [...filteredIncomes].sort((a, b) => {
    let comparison = 0;
    switch (sortBy) {
      case "date":
        comparison = new Date(a.date).getTime() - new Date(b.date).getTime();
        break;
      case "amount":
        comparison = a.amount - b.amount;
        break;
      case "category":
        comparison = a.category.localeCompare(b.category);
        break;
    }
    return sortOrder === "asc" ? comparison : -comparison;
  });

  const paginatedIncomes = sortedIncomes.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  const totalAmount = filteredIncomes.reduce((sum, income) => sum + income.amount, 0);

  const handleEditIncome = (income: Income) => {
    setSelectedIncome(income);
    setDrawerMode("edit");
    setDrawerOpen(true);
  };

  const handleAddIncome = () => {
    setSelectedIncome(undefined);
    setDrawerMode("add");
    setDrawerOpen(true);
  };

  const handleSaveIncome = (
    id: string,
    date: Date,
    amount: number,
    description: string,
    categoryTitle: string
  ) => {
    if (drawerMode === "add") {
      createIncome(
        { date, amount, description, categoryTitle },
        {
          onSuccess: () => {
            setDrawerOpen(false);
            onIncomeChange?.();
          }
        }
      );
    } else {
      updateIncome(
        { id, date, amount, description, categoryTitle },
        {
          onSuccess: () => {
            setDrawerOpen(false);
            onIncomeChange?.();
          }
        }
      );
    }
  };


  const handleSaveAndAddAnother = (
    date: Date,
    amount: number,
    description: string,
    categoryTitle: string
  ) => {
    if (drawerMode === "add") {
      createIncome(
        { date, amount, description, categoryTitle },
        {
          onSuccess: () => {
            setDrawerOpen(true);
            onIncomeChange?.();
          }
        }
      );
    }
  }

  const handleDeleteIncome = (income: Income) => {
    deleteIncome(
      { id: income.id },
      {
        onSuccess: () => {
          setDrawerOpen(false);
          onIncomeChange?.();
        }
      }
    );
  };

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Skeleton variant="rectangular" width={200} height={40} />
          <Skeleton variant="rectangular" width={120} height={36} />
        </Box>
        {[...Array(5)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={60} sx={{ mb: 1 }} />
        ))}
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <ErrorAlert
          error={error}
          helpText={helpText}
          onClear={clearError}
          onRetry={() => refetchIncomes()}
          title="Income Table Error"
        />
      </Box>
    );
  }

  return (
    <Fade in={true} timeout={400}>
      <Box>
        {/* Header with stats and actions */}
        <Box sx={{ p: { xs: 2, sm: 3 }, borderBottom: `1px solid ${theme.palette.divider}` }}>
          <Box sx={{ 
            display: 'flex', 
            flexDirection: { xs: 'column', sm: 'row' },
            justifyContent: 'space-between', 
            alignItems: { xs: 'flex-start', sm: 'center' }, 
            gap: 2, 
            mb: 2 
          }}>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 1, fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
                Income for {month} {year}
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 }, flexWrap: 'wrap' }}>
                <Chip 
                  label={`${filteredIncomes.length} transactions`}
                  variant="outlined"
                  size="small"
                />
                <Chip 
                  label={`Total: ${formatCurrency(totalAmount)}`}
                  color="success"
                  sx={{ fontWeight: 600 }}
                />
              </Box>
            </Box>
            
            <GradientButton
              startIcon={<AddIcon />}
              onClick={handleAddIncome}
              size="small"
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
                '&:hover': {
                  background: `linear-gradient(135deg, ${theme.palette.success.dark}, ${theme.palette.success.main})`},
                width: { xs: '100%', sm: 'auto' }
              }}
            >
              Add Income
            </GradientButton>
          </Box>

          {/* Filters */}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              variant="outlined"
              startIcon={<FilterListIcon />}
              onClick={(e) => setAnchorEl(e.currentTarget)}
              size="small"
              sx={{ borderRadius: 2 }}
            >
              Category Filter
              {selectedCategory && (
                <Chip 
                  label={selectedCategory} 
                  size="small" 
                  onDelete={clearCategoryFilter}
                  sx={{ ml: 1, height: 20 }}
                />
              )}
            </Button>
          </Box>
        </Box>

        {/* Table */}
        {filteredIncomes.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <Avatar
              sx={{
                width: 64,
                height: 64,
                mx: 'auto',
                mb: 3,
                background: `linear-gradient(135deg, ${theme.palette.success.main}, ${theme.palette.success.dark})`,
              }}
            >
              <IncomeIcon sx={{ fontSize: '2rem' }} />
            </Avatar>
            <Typography variant="h6" color="text.secondary" gutterBottom>
              No income found
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {selectedCategory 
                ? `No income found for category "${selectedCategory}"`
                : `No income recorded for ${month} ${year}`
              }
            </Typography>
            <GradientButton
              startIcon={<AddIcon />}
              onClick={handleAddIncome}
            >
              Add Your First Income
            </GradientButton>
          </Box>
        ) : (
          <>
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table sx={{ minWidth: { xs: 650, sm: 'auto' } }}>
                <TableHead>
                  <TableRow sx={{ backgroundColor: theme.palette.grey[50] }}>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "date"}
                        direction={sortBy === "date" ? sortOrder : "asc"}
                        onClick={() => handleSort("date")}
                        sx={{ fontWeight: 600 }}
                      >
                        Date
                      </TableSortLabel>
                    </TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>
                      <TableSortLabel
                        active={sortBy === "category"}
                        direction={sortBy === "category" ? sortOrder : "asc"}
                        onClick={() => handleSort("category")}
                        sx={{ fontWeight: 600 }}
                      >
                        Category
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="right">
                      <TableSortLabel
                        active={sortBy === "amount"}
                        direction={sortBy === "amount" ? sortOrder : "asc"}
                        onClick={() => handleSort("amount")}
                        sx={{ fontWeight: 600 }}
                      >
                        Amount
                      </TableSortLabel>
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600 }}>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedIncomes.map((income, index) => (
                    <TableRow 
                      key={income.id}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: theme.palette.action.hover},
                        '&:nth-of-type(odd)': {
                          backgroundColor: theme.palette.action.selected + '30'}}}
                    >
                      <TableCell sx={{ fontWeight: 500 }}>
                        {formatDate(income.date)}
                      </TableCell>
                      <TableCell>{income.description}</TableCell>
                      <TableCell>
                        <Chip 
                          label={income.category}
                          size="small"
                          variant="outlined"
                          sx={{ borderRadius: 2 }}
                        />
                      </TableCell>
                      <TableCell align="right" sx={{ fontWeight: 600, color: theme.palette.success.main }}>
                        {formatCurrency(income.amount)}
                      </TableCell>
                      <TableCell align="center">
                        <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
                          <Tooltip title="Edit income">
                            <IconButton
                              size="small"
                              onClick={() => handleEditIncome(income)}
                              sx={{ 
                                color: theme.palette.info.main,
                                '&:hover': { backgroundColor: theme.palette.info.light + '20' }
                              }}
                            >
                              <EditIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete income">
                            <IconButton
                              size="small"
                              onClick={() => handleDeleteIncome(income)}
                              sx={{ 
                                color: theme.palette.error.main,
                                '&:hover': { backgroundColor: theme.palette.error.light + '20' }
                              }}
                            >
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              component="div"
              count={sortedIncomes.length}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              rowsPerPageOptions={[10]}
              sx={{ borderTop: `1px solid ${theme.palette.divider}` }}
            />
          </>
        )}

        {/* Category filter menu */}
        <Menu
          anchorEl={anchorEl}
          open={Boolean(anchorEl)}
          onClose={() => setAnchorEl(null)}
          PaperProps={{
            sx: { maxHeight: 300, minWidth: 200 }
          }}
        >
          <MenuItem onClick={clearCategoryFilter}>
            <Typography sx={{ fontStyle: 'italic' }}>All Categories</Typography>
          </MenuItem>
          {categories.map((category) => (
            <MenuItem
              key={category.id}
              onClick={() => handleCategoryFilter(category.title)}
              selected={selectedCategory === category.title}
            >
              {category.title}
            </MenuItem>
          ))}
        </Menu>

        {/* Income form drawer */}
        <IncomeFormDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          onSave={handleSaveIncome}
          onSaveAndAdd={handleSaveAndAddAnother}
          onDelete={handleDeleteIncome}
          mode={drawerMode}
          initialData={selectedIncome}
          selectedYear={year}
          selectedMonth={getMonthIndex(month)}
          categories={categories}
        />
      </Box>
    </Fade>
  );
};

export default IncomeTableComponent;
